import { Stack, Typography } from "@mui/material";

import React, { useState } from "react";
import CaptionAndFilter from "./component/CaptionAndFilter";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { useParams } from "react-router-dom";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import { VITAL_SIGNS_GRAPH } from "utils/reactQueryKeys";
import { useSanitizeQueryParams } from "utils/sanitizeQuery";
import CustomLoader from "components/atoms/CustomLoader";
import CustomGraphTooltip from "./component/CustomGraphTooltip";
import CustomRefLine from "./component/CustomRefLine";

const Graph = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data?.graph ?? []}>
        <defs>
          <linearGradient id="gradientFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="10%" stopColor="rgba(1, 158, 255, 0.4)" />
            <stop offset="100%" stopColor="rgba(1, 158, 255, 0)" />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="xName" />
        <YAxis
          unit={data?.range?.unit}
          domain={["auto", Number(data?.range?.normal?.max || 0) + 5]}
          allowDecimals
          tickCount={10}
          width={110}
        />
        <Tooltip content={<CustomGraphTooltip yName="Rate" />} />

        <ReferenceLine
          y={Number(data?.range?.normal?.max ?? 0)}
          label={
            <CustomRefLine
              range={`${data?.range?.normal?.min}${data?.range?.unit} - ${data?.range?.normal?.max}${data?.range?.unit}`}
            />
          }
          stroke="#00843566"
          strokeDasharray="5 5"
        />
        <Area
          type="monotone"
          dataKey="yValue"
          unit={data?.range?.unit}
          stroke="#019EFF66"
          strokeWidth={"3px"}
          fill="url(#gradientFill)"
          dot={false}
        />
        <ReferenceLine
          y={Number(data?.range?.normal?.min ?? 0)}
          label=""
          stroke="#00843566"
          strokeDasharray="5 5"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const VITALS_CHECKED = "respiratoryRate";

function RespirationRate() {
  const { id } = useParams();

  const [filter, setfilter] = useState({
    name: "This Month",
    value: "ThisMonth",
  });

  //get vital signs
  const {
    data: vitalsGraph,
    isLoading,
    isError,
  } = useCustomQuery(
    [
      VITAL_SIGNS_GRAPH,
      id,
      {
        timeType: filter.value,
        vitalSign: VITALS_CHECKED,
      },
    ],
    {
      url: useSanitizeQueryParams(
        `/visit/patients/${id}/vitals-graph?timeType=${filter.value}&vitalSign=${VITALS_CHECKED}`
      ),
      method: "get",
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const handleDropdown = (item) => {
    setfilter(item);
  };
  return (
    <Stack
      spacing={4}
      sx={{
        border: {
          lg: "1px solid rgba(196, 196, 196, 0.25)",
        },
        padding: {
          xs: 0,
          lg: 3,
        },
        borderRadius: {
          xs: "none",
          lg: "12px",
        },
      }}
    >
      <CaptionAndFilter
        filter={filter}
        handleDropdown={handleDropdown}
        icon={
          <svg
            width="28"
            height="27"
            viewBox="0 0 28 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.7833 9.09965C11.1722 7.01004 9.97898 5.73456 7.78568 5.66576C4.02843 5.54786 2.03174 10.4205 1.13098 13.7217C0.330463 16.6555 -1.89215 28.1383 4.15831 26.6564C5.01292 26.447 5.93342 26.0558 6.83494 25.5585C13.0329 22.1394 12.7271 19.9377 12.2476 13.7861L13.9997 12.0341L15.7517 13.7862C15.3006 19.5746 15.1171 22.2226 21.1644 25.5585C22.6268 26.3651 24.7507 27.2235 26.2631 26.6259C29.4897 25.3512 27.5123 16.0818 26.8684 13.7217C25.9676 10.4205 23.9709 5.54786 20.2137 5.66571C18.1079 5.73183 16.8143 7.06544 16.2634 8.85984C16.3338 8.88937 16.4029 8.92262 16.4613 8.94712C17.1297 9.22729 19.1206 11.2405 19.8281 13.177C19.9008 12.7356 19.9967 12.2618 20.1194 11.7518C20.1495 11.6253 20.2286 11.516 20.3393 11.4478C20.45 11.3797 20.5832 11.3583 20.7097 11.3884C20.8361 11.4185 20.9454 11.4975 21.0136 11.6082C21.0817 11.7189 21.1031 11.8522 21.073 11.9786C20.7882 13.163 20.658 14.1276 20.6374 14.9244C20.956 15.2345 21.422 15.4867 22.1002 15.5149C22.2298 15.5207 22.3519 15.5775 22.4397 15.6729C22.5275 15.7684 22.5739 15.8947 22.5689 16.0243C22.5638 16.1539 22.5076 16.2763 22.4126 16.3645C22.3175 16.4528 22.1914 16.4999 22.0618 16.4955C21.5217 16.4729 21.0752 16.3508 20.7065 16.17C20.9241 17.6006 21.5937 18.2861 22.1595 18.8655C22.9032 19.6268 23.505 20.2434 23.1665 21.6584C23.1595 21.6972 23.0273 22.4211 22.7532 22.782C22.6747 22.8855 22.5583 22.9536 22.4295 22.9712C22.3008 22.9889 22.1703 22.9547 22.0668 22.8762C21.9633 22.7977 21.8952 22.6812 21.8776 22.5525C21.8599 22.4238 21.8941 22.2933 21.9726 22.1898C22.106 22.0139 22.1768 21.5666 22.2045 21.4542L22.2056 21.4544C22.4295 20.5468 21.9943 20.1012 21.4559 19.5499C20.6504 18.7252 19.6659 17.7167 19.6458 15.2814C18.231 13.384 19.0669 11.8859 16.0397 9.85652C16.0105 10.0516 15.9888 10.2499 15.9745 10.4506L15.2578 9.73397V1.43251C15.2578 0.321865 13.9051 -0.250658 13.1108 0.543623C12.883 0.771342 12.7416 1.08607 12.7416 1.43251V9.73397L12.025 10.4506C12.0165 10.3314 12.0047 10.2125 11.9897 10.0939C8.96824 11.8622 9.75886 13.3987 8.35503 15.2814C8.33502 17.7169 7.35042 18.7252 6.54488 19.5499C6.00648 20.1012 5.57133 20.5468 5.79522 21.4544L5.79631 21.4542C5.8241 21.5666 5.89475 22.0139 6.02819 22.1898C6.10413 22.2935 6.1363 22.4229 6.11776 22.55C6.09921 22.6772 6.03144 22.792 5.92906 22.8697C5.82667 22.9473 5.69786 22.9817 5.5704 22.9653C5.44293 22.9489 5.32702 22.883 5.24763 22.782C4.9736 22.4211 4.84131 21.6972 4.83436 21.6585C4.49585 20.2434 5.09763 19.6268 5.84127 18.8655C6.40712 18.2861 7.07671 17.6007 7.29431 16.1699C6.92556 16.3508 6.47914 16.4729 5.93905 16.4955C5.80942 16.4999 5.68331 16.4528 5.58829 16.3645C5.49327 16.2763 5.43708 16.1539 5.43199 16.0243C5.42691 15.8947 5.47336 15.7684 5.56117 15.6729C5.64899 15.5775 5.77102 15.5207 5.9006 15.5149C6.57884 15.4867 7.04478 15.2345 7.36338 14.9244C7.34282 14.1276 7.21261 13.1629 6.9278 11.9786C6.89772 11.8522 6.9191 11.7189 6.98726 11.6082C7.05541 11.4975 7.16474 11.4185 7.2912 11.3884C7.41766 11.3583 7.55089 11.3797 7.66158 11.4478C7.77227 11.516 7.85136 11.6253 7.88144 11.7518C8.00421 12.2624 8.10019 12.7367 8.17298 13.1788C8.95359 11.0805 11.3095 9.28657 11.7834 9.09965H11.7833Z"
              fill="#019EFF"
            />
          </svg>
        }
        title={"Respiration Rate"}
        subTitle={"Number of breaths per minute."}
      />
      <Stack
        sx={{
          width: {
            xs: "70vw",
            sm: "50vw",
          },
          height: {
            xs: "35vh",
            sm: "40vh",
            md: "70vh",
          },
        }}
      >
        {isLoading ? (
          <CustomLoader />
        ) : !isError ? (
          <Graph data={vitalsGraph.data} />
        ) : (
          "No data to display"
        )}
        {!isLoading && !isError && (
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
              opacity: 0.7,
              textAlign: "center",
            }}
          >
            {filter.name}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export default RespirationRate;
